import React, { useEffect, useState } from 'react';
import { CreditCard, Check, Zap, Building2, Star } from 'lucide-react';
import { subscriptionAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const PLAN_ICONS = { basic: Building2, pro: Zap, enterprise: Star };

const BillingPage = () => {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [tab, setTab] = useState('plans');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [plansRes, currentRes] = await Promise.all([
          subscriptionAPI.getPlans(),
          subscriptionAPI.getCurrent()
        ]);
        setPlans(plansRes.data.data || []);
        setCurrent(currentRes.data.data);
      } catch {
        toast.error('Failed to load billing data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleUpgrade = async (plan) => {
    setUpgrading(plan);
    try {
      const res = await subscriptionAPI.upgrade({ plan, billingCycle });
      toast.success(`Upgraded to ${plan} plan!`);
      const currentRes = await subscriptionAPI.getCurrent();
      setCurrent(currentRes.data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upgrade failed');
    } finally {
      setUpgrading('');
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? Access will continue until the end of the billing period.')) return;
    try {
      await subscriptionAPI.cancel('User requested cancellation');
      toast.success('Subscription cancelled');
      const currentRes = await subscriptionAPI.getCurrent();
      setCurrent(currentRes.data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Cancellation failed');
    }
  };

  if (loading) return <div className="loading-center"><div className="spinner spinner-lg" /></div>;

  const currentPlan = current?.subscription?.plan || current?.tenant?.subscription?.plan;
  const currentStatus = current?.subscription?.status || current?.tenant?.subscription?.status;

  const planColors = { basic: '#6B7280', pro: '#3B82F6', enterprise: '#8B5CF6' };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Billing & Subscription</h1>
          <p className="page-subtitle">Manage your plan, billing cycle, and invoices</p>
        </div>
      </div>

      {/* Current Plan Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #EFF6FF, #F5F3FF)',
        border: '1px solid #DBEAFE', borderRadius: 12, padding: '20px 24px',
        marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <div>
          <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 4 }}>Current Plan</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800, color: planColors[currentPlan], textTransform: 'uppercase' }}>
              {currentPlan || 'Basic'}
            </span>
            <span className={`badge ${currentStatus === 'active' ? 'badge-green' : currentStatus === 'trial' ? 'badge-yellow' : 'badge-red'}`}>
              {currentStatus}
            </span>
          </div>
          {current?.tenant?.subscription?.currentPeriodEnd && (
            <div style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>
              Renews: {new Date(current.tenant.subscription.currentPeriodEnd).toLocaleDateString()}
            </div>
          )}
          {currentStatus === 'trial' && current?.tenant?.subscription?.trialEndsAt && (
            <div style={{ fontSize: 13, color: '#D97706', marginTop: 4 }}>
              ⏰ Trial ends: {new Date(current.tenant.subscription.trialEndsAt).toLocaleDateString()}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 13, color: '#6B7280' }}>Usage</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>
              <span style={{ fontWeight: 600 }}>{current?.tenant?.usage?.propertiesCount || 0}</span>
              <span style={{ color: '#9CA3AF' }}> / {current?.tenant?.limits?.maxProperties === 999 ? '∞' : current?.tenant?.limits?.maxProperties} properties</span>
            </div>
            <div style={{ fontSize: 13 }}>
              <span style={{ fontWeight: 600 }}>{current?.tenant?.usage?.usersCount || 0}</span>
              <span style={{ color: '#9CA3AF' }}> / {current?.tenant?.limits?.maxUsers === 999 ? '∞' : current?.tenant?.limits?.maxUsers} users</span>
            </div>
          </div>
        </div>
      </div>

      <div className="tabs">
        {[['plans', 'Upgrade Plans'], ['invoices', 'Invoices']].map(([key, label]) => (
          <button key={key} className={`tab ${tab === key ? 'active' : ''}`} onClick={() => setTab(key)}>{label}</button>
        ))}
      </div>

      {tab === 'plans' && (
        <>
          {/* Billing cycle toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
            <span style={{ fontSize: 14, fontWeight: 500, color: billingCycle === 'monthly' ? '#111827' : '#9CA3AF' }}>Monthly</span>
            <button
              onClick={() => setBillingCycle(p => p === 'monthly' ? 'annual' : 'monthly')}
              style={{
                width: 48, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer',
                background: billingCycle === 'annual' ? '#3B82F6' : '#D1D5DB',
                position: 'relative', transition: 'background 0.2s'
              }}
            >
              <div style={{
                width: 20, height: 20, borderRadius: '50%', background: '#fff',
                position: 'absolute', top: 3,
                left: billingCycle === 'annual' ? 25 : 3,
                transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }} />
            </button>
            <span style={{ fontSize: 14, fontWeight: 500, color: billingCycle === 'annual' ? '#111827' : '#9CA3AF' }}>
              Annual <span style={{ background: '#ECFDF5', color: '#059669', padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600 }}>Save 17%</span>
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
            {plans.map(plan => {
              const Icon = PLAN_ICONS[plan.id] || Building2;
              const isCurrentPlan = plan.id === currentPlan;
              const price = billingCycle === 'annual' ? plan.annualPrice : plan.monthlyPrice;
              const perMonth = billingCycle === 'annual' ? Math.round(plan.annualPrice / 12) : plan.monthlyPrice;

              return (
                <div key={plan.id} style={{
                  background: '#fff', borderRadius: 14, border: plan.popular ? `2px solid ${planColors[plan.id]}` : '1px solid #E5E7EB',
                  padding: '28px 24px', position: 'relative', boxShadow: plan.popular ? '0 8px 24px rgba(59,130,246,0.12)' : 'none'
                }}>
                  {plan.popular && (
                    <div style={{
                      position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                      background: planColors[plan.id], color: '#fff', padding: '4px 16px',
                      borderRadius: 20, fontSize: 12, fontWeight: 600
                    }}>
                      Most Popular
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: `${planColors[plan.id]}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon size={20} color={planColors[plan.id]} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 16, textTransform: 'capitalize' }}>{plan.name}</div>
                    </div>
                  </div>

                  <div style={{ marginBottom: 20 }}>
                    <span style={{ fontSize: 36, fontWeight: 800, color: '#111827' }}>${perMonth}</span>
                    <span style={{ fontSize: 14, color: '#9CA3AF' }}>/mo</span>
                    {billingCycle === 'annual' && (
                      <div style={{ fontSize: 12, color: '#059669', marginTop: 4 }}>
                        ${price}/yr · Save ${plan.annualSavings}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
                    {plan.highlights.map(h => (
                      <div key={h} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#374151' }}>
                        <Check size={14} color={planColors[plan.id]} strokeWidth={3} />
                        {h}
                      </div>
                    ))}
                  </div>

                  {isCurrentPlan ? (
                    <div style={{ textAlign: 'center', padding: '10px', background: `${planColors[plan.id]}10`, borderRadius: 8, color: planColors[plan.id], fontWeight: 600, fontSize: 14 }}>
                      ✓ Current Plan
                    </div>
                  ) : (
                    <button
                      className="btn btn-primary"
                      style={{ width: '100%', justifyContent: 'center', background: planColors[plan.id] }}
                      onClick={() => handleUpgrade(plan.id)}
                      disabled={upgrading === plan.id}
                    >
                      {upgrading === plan.id ? <><div className="spinner" style={{ borderTopColor: '#fff' }} /> Upgrading...</> : `Upgrade to ${plan.name}`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {currentStatus === 'active' && (
            <div style={{ marginTop: 32, padding: '16px 20px', background: '#FEF2F2', borderRadius: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 500, color: '#374151' }}>Cancel Subscription</div>
                <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>Your access continues until the end of the billing period.</div>
              </div>
              <button className="btn btn-danger btn-sm" onClick={handleCancel}>Cancel Subscription</button>
            </div>
          )}
        </>
      )}

      {tab === 'invoices' && (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr><th>Invoice #</th><th>Amount</th><th>Status</th><th>Date</th><th>Download</th></tr>
              </thead>
              <tbody>
                {(current?.subscription?.invoices || []).length === 0 ? (
                  <tr><td colSpan={5}>
                    <div className="empty-state"><CreditCard size={40} /><h3>No invoices yet</h3></div>
                  </td></tr>
                ) : current.subscription.invoices.map((inv, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{inv.invoiceNumber || `INV-${i + 1}`}</td>
                    <td style={{ fontWeight: 600 }}>${inv.amount?.toLocaleString()}</td>
                    <td><span className={`badge ${inv.status === 'paid' ? 'badge-green' : 'badge-red'}`}>{inv.status}</span></td>
                    <td>{inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : '—'}</td>
                    <td>
                      {inv.pdfUrl && (
                        <a href={inv.pdfUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-secondary">
                          Download PDF
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingPage;
